import { observer } from 'mobx-react-lite';
import { useNavigate, useParams, Outlet } from 'react-router';
import { useStore } from '../../../hooks/useStore';
import { exportFlows } from '../config';
import { Box, IconButton, Paper, Typography } from '@mui/material';
import { Page } from '../../../lib/components/compound/pages/page';
import { ContentHead } from '../../../lib/components';
import { ArrowBackwardIcon, ArrowForwardIconSvg, CloseIcon } from '../../../lib/icons';
import { VerticalStepper } from '../../../lib/components/compound/progress/stepper';
import { useTranslation } from 'react-i18next';
import { ContentBox } from '../../../lib/components/compound/pages/contentBox';
import { enqueueSnackbar } from 'notistack';
import { ApiError } from '@pxtra-client-lib-legacy/gen';
import { useExportCreate } from '../../../queries/impex/mutations';
import { Button } from '../../../lib/components/atomic/button';

export const ExportFlow = observer(() => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { type, step } = useParams();
  const { exportFlowStore, companyStore } = useStore();

  if (!exportFlowStore.getIsInitialized(type as keyof typeof exportFlows)) {
    exportFlowStore.initializeFlow(type as keyof typeof exportFlows);
  }

  const currentStep = exportFlowStore.getCurrentStep(parseInt(step as string) - 1);

  if (!type) {
    navigate(`/exports/overview`);
  }

  const { mutateAsync: createExport, isPending: isCreatingExport } = useExportCreate({
    onSuccess: () => {
      enqueueSnackbar(t('export:CREATED'), { variant: 'success' });
      exportFlowStore.resetFlow();
      const lastStep = parseInt(step ?? '') + 1;
      navigate(`/exports/${type}/${lastStep}`); // Navigate to conclusion step
    },
    onError: (error: ApiError) => {
      let message = '';
      if ('body' in error && error.body) {
        message =
          error.body.errors?.non_field_errors?.[0] ??
          error.body.non_field_errors?.[0] ??
          error.body.message ??
          '';
      }
      enqueueSnackbar(message ?? t('export:ERROR'), {
        variant: 'error',
        autoHideDuration: 15000
      });
    }
  });

  const isLastStep =
    exportFlowStore.currentStep === exportFlows[type as keyof typeof exportFlows].steps.length - 1;

  const isSubmitStep =
    exportFlowStore.currentStep === exportFlows[type as keyof typeof exportFlows].steps.length - 2;

  const handleCancelClick = () => {
    navigate(`/exports`);
  };

  const handleNext = async () => {
    const isValid = await exportFlowStore.validateCurrentStep();
    if (isValid !== true) {
      enqueueSnackbar(t(`export:${isValid}`), { variant: 'error' });
      return;
    }
    const currentStep = parseInt(step ?? '') - 1;
    const totalSteps = exportFlows[type as keyof typeof exportFlows].steps.length;
    const nextStep = Math.min(currentStep + 1, totalSteps);
    if (isSubmitStep) {
      await createExport({
        request: exportFlowStore.buildExportRequest(companyStore.company?.id ?? 'MISSING_ID')
      });
    } else {
      navigate(`/exports/${type}/${nextStep + 1}`);
    }
  };

  const handleBack = () => {
    const prevStep = exportFlowStore.currentStep - 1;
    navigate(`/exports/${type}/${prevStep + 1}`);
  };

  return (
    <Page
      sx={{
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '2rem'
        }}
      >
        <Typography variant="h1" fontWeight="bold" lineHeight="2.375rem">
          {t('export:CREATE_TITLE', { context: type })}
        </Typography>
        <IconButton tabIndex={-1} sx={{ padding: 0 }} onClick={handleCancelClick}>
          <CloseIcon sx={{ fontSize: '36px' }} />
        </IconButton>
      </Box>
      <Box
        sx={{
          height: '100%',
          display: 'grid',
          gridTemplateColumns: 'minmax(min-content,320px) auto',
          columnGap: '2rem'
        }}
      >
        <Box sx={{ maxWidth: '320px', alignSelf: 'flex-start' }}>
          <Paper sx={{ padding: '1.5rem 1rem' }}>
            <VerticalStepper
              activeStep={exportFlowStore.currentStep}
              config={
                (exportFlows[type as keyof typeof exportFlows].steps &&
                  Array.isArray(exportFlows[type as keyof typeof exportFlows].steps) &&
                  exportFlows[type as keyof typeof exportFlows].steps.map((step, index) => ({
                    title: step.title ? t(step.title) : '',
                    icon: () => (
                      <Typography paragraph marginBottom="unset">
                        {index + 1}
                      </Typography>
                    )
                  }))) ||
                []
              }
            />
          </Paper>
          <Button variant="secondary" onClick={handleCancelClick} className="w-full mt-6">
            {t('export:BACK_TO_OVERVIEW')}
          </Button>
        </Box>
        <Box className="flex flex-col mb-6">
          <ContentBox
            headComponent={
              (currentStep?.headerTitle && (
                <ContentHead
                  headline={t(currentStep.headerTitle)}
                  showActions={
                    type === 'payroll_data' &&
                    currentStep.headerTitle === 'export:CREATE_SECTION_TITLE_EXPORT_OPTIONS'
                  }
                  renderActions={() => (
                    <Button
                      variant="secondary"
                      onClick={() => window.open('/benefits/settings/payroll', '_blank')}
                    >
                      {t('export:TO_SALARY_TYPE_MAPPING')}
                    </Button>
                  )}
                />
              )) ||
              undefined
            }
            footerComponent={
              <>
                {!isLastStep ? (
                  <Box className="w-full flex justify-between items-center mx-auto gap-x-8 border-4 border-grey-200">
                    <Button
                      variant="tertiary"
                      onClick={handleBack}
                      disabled={exportFlowStore.currentStep === 0}
                    >
                      <ArrowBackwardIcon />
                      {t('common:BACK')}
                    </Button>
                    <Button disabled={isLastStep || isCreatingExport} onClick={handleNext}>
                      {t('common:NEXT')}
                      <ArrowForwardIconSvg />
                    </Button>
                  </Box>
                ) : null}
              </>
            }
          >
            <Outlet />
          </ContentBox>
        </Box>
      </Box>
    </Page>
  );
});
